// src/fleet-stats/analytics/buildInsights.js
import { IDLE_UTILIZATION_THRESHOLD, INACTIVE_CUTOFF_DAYS, ANOMALY_DROP_THRESHOLD, BASELINE_WINDOW_DAYS, RECENT_WINDOW_DAYS } from "../constants.js";
import { addDays, daysBetween, detectOperatesWeekends, detectSiteOperatesWeekends, smartWeekdayFilter } from "../utils/date.js";

/**
 * Build insight findings for the Insights panel.
 * Weekend handling: per-robot detection with site-level fallback throughout.
 */
export function buildInsights(rows, maxDate) {
    if (!maxDate || rows.length === 0) return [];

    const findings = [];

    const recentFrom   = addDays(maxDate, -(RECENT_WINDOW_DAYS - 1));
    const baselineFrom = addDays(maxDate, -(BASELINE_WINDOW_DAYS + RECENT_WINDOW_DAYS - 1));
    const baselineTo   = addDays(maxDate, -RECENT_WINDOW_DAYS);
    const offlineCutoff = addDays(maxDate, -(INACTIVE_CUTOFF_DAYS - 1));

    // ── Site-level weekend patterns ──────────────────────────────────────────
    const bySiteAll = new Map();
    for (const r of rows) {
        if (!bySiteAll.has(r.siteName)) bySiteAll.set(r.siteName, []);
        bySiteAll.get(r.siteName).push(r);
    }
    const siteWeekendPattern = new Map();
    for (const [siteName, siteRows] of bySiteAll.entries()) {
        siteWeekendPattern.set(siteName, detectSiteOperatesWeekends(siteRows));
    }

    // ── Group by robot ───────────────────────────────────────────────────────
    const byRobot = new Map();
    for (const r of rows) {
        if (!byRobot.has(r.robotId)) {
            byRobot.set(r.robotId, {
                robotId: r.robotId, robotName: r.robotName,
                client: r.client, siteName: r.siteName,
                allDays: [], recentDays: [], baselineDays: [],
            });
        }
        const e = byRobot.get(r.robotId);
        e.allDays.push(r);
        if (r.date >= recentFrom)                           e.recentDays.push(r);
        if (r.date >= baselineFrom && r.date <= baselineTo) e.baselineDays.push(r);
    }

    const avg = (arr, fn) =>
        arr.length === 0 ? 0 : arr.reduce((s, r) => s + fn(r), 0) / arr.length;

    // ── 1. Robots with big drop in driving time ──────────────────────────────
    const droppedRobots = [];
    for (const e of byRobot.values()) {
        if (e.baselineDays.length < 5) continue;

        const robotWknd = detectOperatesWeekends(e.allDays);
        const siteWknd  = siteWeekendPattern.get(e.siteName) ?? null;

        const bFiltered = smartWeekdayFilter(e.baselineDays, robotWknd, siteWknd);
        const rFiltered = smartWeekdayFilter(e.recentDays,   robotWknd, siteWknd);

        const bBase   = avg(bFiltered, (r) => r.moving_time_s);
        const bRecent = avg(rFiltered, (r) => r.moving_time_s);

        if (bBase < 300) continue;
        const ratio = bBase > 0 ? bRecent / bBase : 1;
        if (ratio < ANOMALY_DROP_THRESHOLD) {
            droppedRobots.push({
                robotId: e.robotId, robotName: e.robotName, client: e.client, siteName: e.siteName,
                pct: Math.round((1 - ratio) * 100),
            });
        }
    }
    if (droppedRobots.length > 0) {
        findings.push({
            type: "drop",
            severity: droppedRobots.length >= 3 ? "critical" : "warning",
            title: `${droppedRobots.length} robot${droppedRobots.length > 1 ? "s" : ""} had a major drop in driving time`,
            detail: droppedRobots.slice(0, 3).map((r) => `${r.robotName} (${r.client} / ${r.siteName}): −${r.pct}%`).join(" · "),
            robots: droppedRobots,
        });
    }

    // ── 2. Powered on but not driving ────────────────────────────────────────
    const idleRobots = [];
    for (const e of byRobot.values()) {
        const recentUptime  = e.recentDays.reduce((s, r) => s + (r.uptime_s || 0), 0);
        const recentDriving = e.recentDays.reduce((s, r) => s + r.moving_time_s, 0);
        // Uptime check is not weekend-filtered — a robot powered on at the weekend
        // when the site doesn't operate is a separate concern (should be off)
        if (recentUptime > 3600 && recentDriving === 0) {
            idleRobots.push({ robotId: e.robotId, robotName: e.robotName, client: e.client, siteName: e.siteName });
        }
    }
    if (idleRobots.length > 0) {
        findings.push({
            type: "idle",
            severity: "warning",
            title: `${idleRobots.length} robot${idleRobots.length > 1 ? "s" : ""} powered on but not driving`,
            detail: idleRobots.slice(0, 3).map((r) => `${r.robotName} (${r.client})`).join(" · "),
            robots: idleRobots,
        });
    }

    // ── 3. Sites with busiest week ever ──────────────────────────────────────
    // Busiest week is based on total km including weekends — we want to capture
    // genuine peak output regardless of operating pattern
    const bestWeekSites = [];
    for (const [siteName, site] of bySiteAll.entries()) {
        const byDate = new Map();
        for (const r of site.days || site) {
            const cur = byDate.get(r.date) || 0;
            byDate.set(r.date, cur + r.distance_m);
        }
        const dates = Array.from(byDate.keys()).sort();
        if (dates.length < 14) continue;

        let maxWeekKm = 0, recentWeekKm = 0;
        for (let i = 6; i < dates.length; i++) {
            const window = dates.slice(i - 6, i + 1);
            const total  = window.reduce((s, d) => s + (byDate.get(d) || 0), 0) / 1000;
            if (total > maxWeekKm) maxWeekKm = total;
            if (i === dates.length - 1) recentWeekKm = total;
        }
        if (recentWeekKm >= maxWeekKm * 0.95 && maxWeekKm > 0) {
            const client = (bySiteAll.get(siteName)?.[0])?.client || "";
            bestWeekSites.push({ siteName, client, km: Math.round(recentWeekKm) });
        }
    }
    if (bestWeekSites.length > 0) {
        findings.push({
            type: "positive",
            severity: "positive",
            title: `${bestWeekSites.length} site${bestWeekSites.length > 1 ? "s" : ""} had their busiest week on record`,
            detail: bestWeekSites.slice(0, 3).map((s) => `${s.siteName} (${s.client}): ${s.km} km`).join(" · "),
        });
    }

    // ── 4. Clients with declining usage (14 days) ────────────────────────────
    // Client-level trend uses raw daily totals — not weekend-filtered since it's
    // an aggregate signal and we don't want to strip legitimate weekend drops
    const byClient = new Map();
    for (const r of rows) {
        if (!byClient.has(r.client)) byClient.set(r.client, []);
        byClient.get(r.client).push(r);
    }
    const decliningClients = [];
    for (const [client, clientRows] of byClient.entries()) {
        const byDate = new Map();
        for (const r of clientRows) {
            const cur = byDate.get(r.date) || 0;
            byDate.set(r.date, cur + r.distance_m);
        }
        const dates = Array.from(byDate.keys()).sort();
        if (dates.length < 14) continue;

        const recent7 = dates.slice(-7).reduce((s, d)  => s + (byDate.get(d) || 0), 0) / 7;
        const prev7   = dates.slice(-14, -7).reduce((s, d) => s + (byDate.get(d) || 0), 0) / 7;

        if (prev7 > 100 && recent7 / prev7 < 0.6) {
            decliningClients.push({ client, pct: Math.round((1 - recent7 / prev7) * 100) });
        }
    }
    if (decliningClients.length > 0) {
        findings.push({
            type: "trend",
            severity: "warning",
            title: `${decliningClients.length} client${decliningClients.length > 1 ? "s" : ""} showing declining usage over 14 days`,
            detail: decliningClients.slice(0, 3).map((c) => `${c.client}: −${c.pct}%`).join(" · "),
        });
    }

    // ── 5. High uptime, low movement ─────────────────────────────────────────
    // Not weekend-filtered — a robot with high uptime and low movement is
    // relevant regardless of operating schedule
    const lowUtilRobots = [];
    for (const e of byRobot.values()) {
        const recentUptime  = e.recentDays.reduce((s, r) => s + (r.uptime_s || 0), 0);
        const recentDriving = e.recentDays.reduce((s, r) => s + r.moving_time_s, 0);
        if (recentUptime < 3600 * 5) continue;
        if (recentDriving === 0) continue;
        const util = recentDriving / recentUptime;
        if (util < IDLE_UTILIZATION_THRESHOLD) {
            lowUtilRobots.push({
                robotId: e.robotId, robotName: e.robotName, client: e.client, siteName: e.siteName,
                util: Math.round(util * 100),
            });
        }
    }
    if (lowUtilRobots.length > 0) {
        findings.push({
            type: "utilization",
            severity: "info",
            title: `${lowUtilRobots.length} robot${lowUtilRobots.length > 1 ? "s" : ""} with high uptime but very low movement`,
            detail: lowUtilRobots.slice(0, 3).map((r) => `${r.robotName} (${r.client}): ${r.util}% utilization`).join(" · "),
            robots: lowUtilRobots,
        });
    }

    // ── 6. Offline / missing data ────────────────────────────────────────────
    const offlineRobots = [];
    for (const e of byRobot.values()) {
        const lastDate = e.allDays.reduce(
            (best, r) => (r.distance_m > 0 || r.uptime_s > 0) && (!best || r.date > best) ? r.date : best,
            null
        );
        if (lastDate && lastDate < offlineCutoff) {
            offlineRobots.push({ robotId: e.robotId, robotName: e.robotName, client: e.client, siteName: e.siteName, lastDate });
        }
    }
    if (offlineRobots.length > 0) {
        findings.push({
            type: "offline",
            severity: "info",
            title: `${offlineRobots.length} robot${offlineRobots.length > 1 ? "s" : ""} with no data in the last ${INACTIVE_CUTOFF_DAYS} days`,
            detail: offlineRobots.slice(0, 3).map((r) => `${r.robotName} (${r.client}): last seen ${r.lastDate}`).join(" · "),
            robots: offlineRobots,
        });
    }

    return findings;
}

/**
 * Build trend comparison: current period vs previous same-length period.
 */
export function buildTrends(rows, dateFrom, dateTo) {
    if (!dateFrom || !dateTo) return null;

    const days    = daysBetween(dateFrom, dateTo) + 1;
    const prevTo  = addDays(dateFrom, -1);
    const prevFrom = addDays(prevTo, -(days - 1));

    const current  = rows.filter((r) => r.date >= dateFrom && r.date <= dateTo);
    const previous = rows.filter((r) => r.date >= prevFrom && r.date <= prevTo);

    const sum          = (arr, fn) => arr.reduce((s, r) => s + fn(r), 0);
    const uniqueRobots = (arr) => new Set(arr.filter((r) => r.distance_m > 0 || r.moving_time_s > 0).map((r) => r.robotId)).size;
    const pct          = (cur, prev) => (prev > 0 ? (cur - prev) / prev : null);

    const cKm     = sum(current,  (r) => r.distance_m)    / 1000;
    const pKm     = sum(previous, (r) => r.distance_m)    / 1000;
    const cHours  = sum(current,  (r) => r.moving_time_s) / 3600;
    const pHours  = sum(previous, (r) => r.moving_time_s) / 3600;
    const cRobots = uniqueRobots(current);
    const pRobots = uniqueRobots(previous);
    const cUptime = sum(current,  (r) => r.uptime_s || 0) / 3600;
    const pUptime = sum(previous, (r) => r.uptime_s || 0) / 3600;

    return {
        prevFrom, prevTo,
        km:           { current: cKm,     previous: pKm,     changePct: pct(cKm,     pKm)     },
        hours:        { current: cHours,  previous: pHours,  changePct: pct(cHours,  pHours)  },
        activeRobots: { current: cRobots, previous: pRobots, changePct: pct(cRobots, pRobots) },
        uptime:       { current: cUptime, previous: pUptime, changePct: pct(cUptime, pUptime) },
    };
}